import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import Typesense, { Client } from 'typesense';
import { SupabaseService } from 'src/app/subscription/services/supabase/supabase.service';
import { environment } from 'src/environments/environment';

// Types for search results
export interface DocumentHit {
  id: string;
  name: string;
  type: string;
  description: string;
  path: string;
  is_favorite: boolean;
  created_at?: number;
  updated_at?: number;
}

export interface PlanHit {
  id: string;
  plan_id: string;
  planner_id: string;
  planner_name: string;
  content: string;
  date: string;
}

export interface UnifiedSearchResult {
  documents: DocumentHit[];
  plans: PlanHit[];
}

interface TypesenseConfig {
  host: string;
  port: number;
  protocol: string;
  apiKey: string;
  collections: string[];
  userId: string;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root'
})
export class TypesenseService {
  private client: Client | null = null;
  private configPromise: Promise<TypesenseConfig> | null = null;
  private configFetchedAt: number = 0;

  private supabaseService = inject(SupabaseService);

  /**
   * Get Typesense config from the Edge Function
   * Caches the config and refreshes when near expiry
   */
  private async getConfig(): Promise<TypesenseConfig> {
    const now = Date.now();

    // Check if we need to refresh (within 5 minutes of expiry or no config)
    if (this.configPromise) {
      const config = await this.configPromise;
      const expiresAt = this.configFetchedAt + (config.expiresIn * 1000);
      const refreshThreshold = 5 * 60 * 1000; // 5 minutes

      if (now < expiresAt - refreshThreshold) {
        return config;
      }
      // Config is stale, clear it
      this.configPromise = null;
      this.client = null;
    }

    // Fetch new config
    this.configFetchedAt = now;
    this.configPromise = this.fetchConfig();
    return this.configPromise;
  }

  private async fetchConfig(): Promise<TypesenseConfig> {
    const { data: { session } } = await this.supabaseService._client.auth.getSession();

    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${environment.SUPABASE_URL}/functions/v1/client-typesense-config`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get Typesense config: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to get Typesense config');
    }

    return result.data as TypesenseConfig;
  }

  /**
   * Get or create Typesense client
   */
  private async getClient(): Promise<Client> {
    const config = await this.getConfig();

    if (!this.client) {
      this.client = new Typesense.Client({
        nodes: [{
          host: config.host,
          port: config.port,
          protocol: config.protocol
        }],
        apiKey: config.apiKey,
        connectionTimeoutSeconds: 5
      });
    }

    return this.client;
  }

  /**
   * Unified search across documents and plans
   * Returns results from both collections grouped by type
   */
  search(query: string, perPage: number = 5): Observable<UnifiedSearchResult> {
    return from(this.performSearch(query, perPage));
  }

  private async performSearch(query: string, perPage: number): Promise<UnifiedSearchResult> {
    if (!query || query.trim().length === 0) {
      return { documents: [], plans: [] };
    }

    const client = await this.getClient();

    const searchRequests = {
      searches: [
        {
          collection: 'documents',
          q: query,
          query_by: 'name,content,description',
          per_page: perPage,
          sort_by: '_text_match:desc,updated_at:desc'
        },
        {
          collection: 'plans',
          q: query,
          query_by: 'content,planner_name',
          per_page: perPage,
          sort_by: '_text_match:desc,updated_at:desc'
        }
      ]
    };

    try {
      const response: any = await client.multiSearch.perform(searchRequests, {});

      const documents: DocumentHit[] = (response.results[0]?.hits || []).map((hit: any) => ({
        id: hit.document.id,
        name: hit.document.name,
        type: hit.document.type,
        description: hit.document.description || '',
        path: hit.document.path || '',
        is_favorite: hit.document.is_favorite || false,
        created_at: hit.document.created_at,
        updated_at: hit.document.updated_at
      }));

      const plans: PlanHit[] = (response.results[1]?.hits || []).map((hit: any) => ({
        id: hit.document.id,
        plan_id: hit.document.plan_id,
        planner_id: hit.document.planner_id,
        planner_name: hit.document.planner_name || '',
        content: hit.document.content || '',
        date: hit.document.date || ''
      }));

      return { documents, plans };
    } catch (error) {
      console.error('Typesense search error:', error);
      return { documents: [], plans: [] };
    }
  }

  /**
   * Search only documents collection, optionally filtered by document type
   * Useful for file picker which only wants type:file documents
   */
  searchDocumentsOnly(query: string, options?: {
    filterByType?: string;
    perPage?: number
  }): Observable<DocumentHit[]> {
    return from(this.performDocumentsSearch(query, options));
  }

  private async performDocumentsSearch(query: string, options?: {
    filterByType?: string;
    perPage?: number
  }): Promise<DocumentHit[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const client = await this.getClient();
    const perPage = options?.perPage ?? 20;

    const searchParams: any = {
      q: query,
      query_by: 'name,content,description',
      per_page: perPage,
      sort_by: '_text_match:desc,updated_at:desc'
    };

    // Add type filter if specified (e.g., 'file' to only get uploaded files)
    if (options?.filterByType) {
      searchParams.filter_by = `type:${options.filterByType}`;
    }

    try {
      const response: any = await client.collections('documents').documents().search(searchParams);

      return (response.hits || []).map((hit: any) => ({
        id: hit.document.id,
        name: hit.document.name,
        type: hit.document.type,
        description: hit.document.description || '',
        path: hit.document.path || '',
        is_favorite: hit.document.is_favorite || false,
        created_at: hit.document.created_at,
        updated_at: hit.document.updated_at
      }));
    } catch (error) {
      console.error('Typesense documents search error:', error);
      return [];
    }
  }
}
